/**
 * #system-health channel — live service health dashboard.
 *
 * Polls GET /api/system-health every 15s. Shows a grid of service cards with
 * status dots (green/amber/red), latency, and VPS resource gauges.
 * Visual reference: Uptime Robot / BetterStack status page.
 */

import { get, API_BASE } from '../api';
import { state } from '../state';

interface ServiceHealth {
  name: string;
  kind: string;
  status: string;
  latency_ms: number;
  detail: string;
}

interface VpsStats {
  cpu_percent?: number;
  memory_used_mb?: number;
  memory_total_mb?: number;
  disk_used_gb?: number;
  disk_total_gb?: number;
  uptime_secs?: number;
  load_avg_1?: number;
  load_avg_5?: number;
  load_avg_15?: number;
  updated_at?: string;
  stale?: boolean;
}

interface SystemHealthResponse {
  services: ServiceHealth[];
  vps: VpsStats | null;
  generated_at: string;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let rootEl: HTMLElement | null = null;

export function cleanupSystemHealth(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  rootEl = null;
}

function formatLocalDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
  } catch { return iso; }
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, c =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'
  );
}

function statusDot(status: string): string {
  const colors: Record<string, string> = {
    ok: '#16a34a',
    degraded: '#f59e0b',
    down: '#dc2626',
  };
  const color = colors[status] || 'var(--fg-muted)';
  return `<span style="display:inline-block;width:10px;height:10px;border-radius:5px;background:${color};flex-shrink:0"></span>`;
}

function kindBadge(kind: string): string {
  const colors: Record<string, string> = {
    backend: 'var(--accent)',
    infra: '#8b5cf6',
    ai: '#06b6d4',
    device: '#f59e0b',
  };
  const bg = colors[kind] || 'var(--surface-0)';
  return `<span style="padding:1px 6px;border-radius:3px;background:${bg};color:var(--bg);font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.3px">${esc(kind)}</span>`;
}

function latencyColor(ms: number): string {
  if (ms < 50) return '#16a34a';
  if (ms < 200) return '#f59e0b';
  return '#dc2626';
}

/** URLs for services that have a web UI accessible from the user's browser. */
const SERVICE_WEB_UI: Record<string, string> = {
  gitea: '/gitea/',
  noVNC: '/novnc/',
};

function serviceCard(svc: ServiceHealth): string {
  const webUrl = SERVICE_WEB_UI[svc.name];
  const openBtn = webUrl
    ? `<a href="${esc(webUrl)}" target="_blank" rel="noopener" style="padding:2px 8px;border-radius:4px;border:1px solid var(--border);background:var(--surface-0);color:var(--fg);font-size:11px;font-weight:600;text-decoration:none;cursor:pointer;white-space:nowrap">Open</a>`
    : '';
  return `
    <div style="border:1px solid var(--border);border-radius:8px;padding:12px 14px;background:var(--bg-elev);display:flex;flex-direction:column;gap:8px">
      <div style="display:flex;align-items:center;gap:8px">
        ${statusDot(svc.status)}
        <span style="font-weight:600;font-size:14px;color:var(--fg);flex:1">${esc(svc.name)}</span>
        ${openBtn}
        ${kindBadge(svc.kind)}
      </div>
      <div style="display:flex;align-items:center;gap:12px;font-size:12px;color:var(--fg-muted)">
        <span style="color:${latencyColor(svc.latency_ms)};font-weight:600;font-family:var(--font-mono,monospace)">${svc.latency_ms}ms</span>
        <span>${esc(svc.detail)}</span>
      </div>
    </div>
  `;
}

function gaugeBar(label: string, used: number, total: number, unit: string): string {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const color = pct > 90 ? '#dc2626' : pct > 70 ? '#f59e0b' : '#16a34a';
  return `
    <div style="border:1px solid var(--border);border-radius:8px;padding:12px 14px;background:var(--bg-elev)">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
        <span style="font-size:12px;color:var(--fg-muted);text-transform:uppercase;letter-spacing:0.3px">${esc(label)}</span>
        <span style="font-size:13px;font-weight:600;color:var(--fg);font-family:var(--font-mono,monospace)">${used.toFixed(1)} / ${total.toFixed(1)} ${esc(unit)}</span>
      </div>
      <div style="height:6px;background:var(--border);border-radius:3px;overflow:hidden">
        <div style="height:100%;width:${pct.toFixed(1)}%;background:${color};border-radius:3px;transition:width 0.3s"></div>
      </div>
    </div>
  `;
}

function formatUptime(secs: number | undefined): string {
  if (!secs || secs <= 0) return '—';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function renderHealth(data: SystemHealthResponse): string {
  const okCount = data.services.filter(s => s.status === 'ok').length;
  const degradedCount = data.services.filter(s => s.status === 'degraded').length;
  const downCount = data.services.filter(s => s.status === 'down').length;
  const total = data.services.length;

  const allOk = downCount === 0 && degradedCount === 0;
  const headerColor = allOk ? '#16a34a' : downCount > 0 ? '#dc2626' : '#f59e0b';
  const headerText = allOk
    ? 'All systems operational'
    : downCount > 0
      ? `${downCount} service${downCount > 1 ? 's' : ''} down`
      : `${degradedCount} service${degradedCount > 1 ? 's' : ''} degraded`;

  // Group by kind
  const groups: Record<string, ServiceHealth[]> = {};
  for (const svc of data.services) {
    const k = svc.kind;
    if (!groups[k]) groups[k] = [];
    groups[k].push(svc);
  }
  // Sort within groups: down first, then degraded, then ok
  const statusOrder: Record<string, number> = { down: 0, degraded: 1, ok: 2 };
  for (const g of Object.values(groups)) {
    g.sort((a, b) => (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3));
  }
  const kindOrder = ['backend', 'infra', 'ai', 'device'];
  const kindLabels: Record<string, string> = {
    backend: 'Backend Services',
    infra: 'Infrastructure',
    ai: 'AI Pipeline',
    device: 'Devices',
  };

  let servicesHtml = '';
  for (const kind of kindOrder) {
    const svcs = groups[kind];
    if (!svcs || svcs.length === 0) continue;
    servicesHtml += `
      <div style="margin-top:18px">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:var(--fg-muted);margin-bottom:8px;font-weight:600">${esc(kindLabels[kind] || kind)}</div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:8px">
          ${svcs.map(serviceCard).join('')}
        </div>
      </div>
    `;
  }

  // VPS gauges
  let vpsHtml = '';
  const v = data.vps;
  if (v) {
    vpsHtml = `
      <div style="margin-top:18px">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:var(--fg-muted);margin-bottom:8px;font-weight:600">VPS Resources${v.stale ? ' <span style="color:#f59e0b">(stale)</span>' : ''}</div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:8px">
          ${v.cpu_percent != null ? `
            <div style="border:1px solid var(--border);border-radius:8px;padding:12px 14px;background:var(--bg-elev)">
              <div style="display:flex;justify-content:space-between;margin-bottom:6px">
                <span style="font-size:12px;color:var(--fg-muted);text-transform:uppercase;letter-spacing:0.3px">CPU</span>
                <span style="font-size:13px;font-weight:600;color:var(--fg);font-family:var(--font-mono,monospace)">${v.cpu_percent.toFixed(1)}%</span>
              </div>
              <div style="height:6px;background:var(--border);border-radius:3px;overflow:hidden">
                <div style="height:100%;width:${Math.min(100, v.cpu_percent).toFixed(1)}%;background:${v.cpu_percent > 90 ? '#dc2626' : v.cpu_percent > 70 ? '#f59e0b' : '#16a34a'};border-radius:3px;transition:width 0.3s"></div>
              </div>
              ${v.load_avg_1 != null ? `<div style="font-size:11px;color:var(--fg-muted);margin-top:4px">Load: ${v.load_avg_1.toFixed(2)} / ${(v.load_avg_5 || 0).toFixed(2)} / ${(v.load_avg_15 || 0).toFixed(2)}</div>` : ''}
            </div>
          ` : ''}
          ${v.memory_total_mb != null && v.memory_used_mb != null ? gaugeBar('Memory', v.memory_used_mb, v.memory_total_mb, 'MB') : ''}
          ${v.disk_total_gb != null && v.disk_used_gb != null ? gaugeBar('Disk', v.disk_used_gb, v.disk_total_gb, 'GB') : ''}
          ${v.uptime_secs != null ? `
            <div style="border:1px solid var(--border);border-radius:8px;padding:12px 14px;background:var(--bg-elev)">
              <div style="font-size:12px;color:var(--fg-muted);text-transform:uppercase;letter-spacing:0.3px;margin-bottom:4px">Uptime</div>
              <div style="font-size:20px;font-weight:700;color:var(--fg);font-family:var(--font-mono,monospace)">${esc(formatUptime(v.uptime_secs))}</div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  return `
    <div style="padding:16px;overflow-y:auto;flex:1;min-height:0;font-family:var(--font-sans);scrollbar-width:thin;scrollbar-color:var(--surface-0) transparent">
      <!-- Header banner -->
      <div style="border:1px solid var(--border);border-radius:8px;padding:14px 18px;background:var(--bg-elev);display:flex;align-items:center;gap:12px">
        <span style="display:inline-block;width:14px;height:14px;border-radius:7px;background:${headerColor}"></span>
        <span style="font-size:18px;font-weight:700;color:var(--fg)">${esc(headerText)}</span>
        <span style="margin-left:auto;font-size:12px;color:var(--fg-muted)">
          ${okCount}/${total} healthy
        </span>
      </div>
      ${servicesHtml}
      ${vpsHtml}
      <div style="text-align:center;color:var(--fg-muted);font-size:11px;margin-top:18px;padding-bottom:12px">
        Refreshes every 15 s · ${esc(formatLocalDate(data.generated_at))}
      </div>
    </div>
  `;
}

async function fetchHealth(): Promise<SystemHealthResponse | null> {
  try {
    return await get<SystemHealthResponse>(`${API_BASE}/api/system-health`);
  } catch {
    return null;
  }
}

export async function renderSystemHealth(container: HTMLElement): Promise<void> {
  cleanupSystemHealth();

  const sid = state.currentServer;
  if (!sid) {
    container.innerHTML = '<div style="padding:20px;color:var(--fg-muted)">Select a server.</div>';
    return;
  }

  container.innerHTML = `
    <div style="flex:1;min-height:0;display:flex;flex-direction:column">
      <div id="sh-root" style="flex:1;min-height:0;display:flex;flex-direction:column"></div>
    </div>
  `;
  rootEl = container.querySelector<HTMLElement>('#sh-root');
  if (!rootEl) return;

  rootEl.innerHTML = `
    <div style="padding:40px 20px;text-align:center;color:var(--fg-muted);font-size:14px">
      Probing services…
    </div>
  `;

  const data = await fetchHealth();
  if (data && rootEl) {
    rootEl.innerHTML = renderHealth(data);
  } else if (rootEl) {
    rootEl.innerHTML = `
      <div style="padding:40px 20px;text-align:center;color:var(--fg-muted)">
        <div style="font-size:16px;margin-bottom:8px">System Health</div>
        <div style="font-size:13px">Failed to load health data. The endpoint may not be deployed yet.</div>
      </div>
    `;
  }

  // Poll every 15s — preserve scroll position across DOM replacement.
  pollTimer = setInterval(async () => {
    const d = await fetchHealth();
    if (d && rootEl) {
      const scroller = rootEl.firstElementChild as HTMLElement | null;
      const scrollY = scroller?.scrollTop ?? 0;
      rootEl.innerHTML = renderHealth(d);
      const newScroller = rootEl.firstElementChild as HTMLElement | null;
      if (newScroller) newScroller.scrollTop = scrollY;
    }
  }, 15_000);
}
